import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/layout/SEO';
import { MaterialIcon } from '@/components/ui/MaterialIcon';

const termsSchema = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Terms of Use',
    description: 'startzone.dev terms of use, platform scope, risk disclaimers, and service conditions.',
});

const sections = [
    {
        id: '01',
        title: 'PLATFORM_SCOPE',
        body: (
            <>
                <p>
                    STARTZONE.DEV is an educational platform for learning developer workflows, concepts, and game-like
                    progression. The content, quests, examples, and guidance are provided for training and exploration,
                    not as professional, legal, security, or production engineering advice.
                </p>
                <p>
                    These terms define the rules for using the platform. Data handling, telemetry, and deletion-request
                    practices are described separately in the privacy notice.
                </p>
                <div className="relative overflow-hidden bg-[#131313] px-5 py-5 md:px-6 md:py-6">
                    <div
                        className="pointer-events-none absolute inset-0 opacity-[0.16]"
                        style={{
                            background:
                                'linear-gradient(to bottom, transparent 50%, rgba(140, 172, 255, 0.08) 50%)',
                            backgroundSize: '100% 4px',
                        }}
                    />
                    <div className="relative mb-4 font-mono text-[9px] uppercase tracking-[0.28em] text-[#8cacff]">
                        [ OPERATING_BOUNDARY ]
                    </div>
                    <ul className="relative space-y-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#d7dae3] sm:text-[11px]">
                        <li className="flex gap-3"><span className="text-[#8cacff]">/</span> EDUCATIONAL_USE_ONLY</li>
                        <li className="flex gap-3"><span className="text-[#8cacff]">/</span> NO_PRODUCTION_WARRANTY</li>
                        <li className="flex gap-3"><span className="text-[#8cacff]">/</span> USER_DECISIONS_REMAIN_USER_RESPONSIBILITY</li>
                    </ul>
                </div>
            </>
        ),
    },
    {
        id: '02',
        title: 'USE_AT_YOUR_OWN_RISK',
        body: (
            <>
                <p>
                    You use the platform, its learning materials, and any code or command examples at your own risk.
                    Content may be incomplete, simplified, outdated, or unsuitable for your environment. You are
                    responsible for evaluating whether any workflow, script, command, or recommendation is safe and
                    appropriate before applying it in a live system.
                </p>
                <div className="grid gap-4 md:grid-cols-2">
                    <div className="bg-[#1a1b1b] px-4 py-4 md:px-5 md:py-5">
                        <div className="mb-3 flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.22em] text-[#8cacff]">
                            <MaterialIcon icon="warning" size={13} className="text-current" />
                            TRAINING_CONTEXT
                        </div>
                        <p className="text-[12px] leading-6 text-[#8a8f99] md:text-[13px]">
                            Lessons are optimized for teaching clarity, not for guaranteeing correctness in every
                            production environment.
                        </p>
                    </div>
                    <div className="bg-[#1a1b1b] px-4 py-4 md:px-5 md:py-5">
                        <div className="mb-3 flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.22em] text-[#00cffc]">
                            <MaterialIcon icon="terminal" size={13} className="text-current" />
                            EXECUTION_RISK
                        </div>
                        <p className="text-[12px] leading-6 text-[#8a8f99] md:text-[13px]">
                            Running commands or adapting examples from the platform is your decision, and any resulting
                            damage, loss, or downtime stays with you.
                        </p>
                    </div>
                </div>
            </>
        ),
    },
    {
        id: '03',
        title: 'SERVICE_AVAILABILITY',
        body: (
            <>
                <p>
                    We do not guarantee uninterrupted availability, error-free operation, permanent storage, or
                    continued access to any feature, quest, account state, or integration. The platform may be changed,
                    paused, restricted, or removed at any time.
                </p>
                <div className="flex flex-wrap gap-2.5 pt-1">
                    {['NO_UPTIME_GUARANTEE', 'FEATURES_MAY_CHANGE', 'STATE_MAY_RESET'].map((label) => (
                        <span
                            key={label}
                            className="bg-[#1a1b1b] px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.18em] text-[#70747c]"
                        >
                            {label}
                        </span>
                    ))}
                </div>
            </>
        ),
    },
    {
        id: '04',
        title: 'USER_CONDUCT',
        body: (
            <>
                <p>
                    You may not use the platform to abuse infrastructure, scrape protected resources, interfere with
                    other users, impersonate another person, attack connected services, automate around access controls,
                    or violate applicable law. We may suspend or terminate access when misuse, fraud, or abuse is
                    detected.
                </p>
                <div className="relative overflow-hidden bg-[#131313] px-5 py-5 md:px-6 md:py-6">
                    <div
                        className="pointer-events-none absolute inset-0 opacity-[0.14]"
                        style={{
                            background:
                                'linear-gradient(to bottom, transparent 50%, rgba(255, 113, 108, 0.08) 50%)',
                            backgroundSize: '100% 4px',
                        }}
                    />
                    <div className="relative mb-4 font-mono text-[9px] uppercase tracking-[0.28em] text-[#ff716c]">
                        [ PROHIBITED_BEHAVIOR ]
                    </div>
                    <ul className="relative space-y-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#d7dae3] sm:text-[11px]">
                        <li className="flex gap-3"><span className="text-[#ff716c]">/</span> NO_INFRASTRUCTURE_ABUSE</li>
                        <li className="flex gap-3"><span className="text-[#ff716c]">/</span> NO_CREDENTIAL_MISUSE</li>
                        <li className="flex gap-3"><span className="text-[#ff716c]">/</span> NO_BYPASS_OF_ACCESS_CONTROLS</li>
                    </ul>
                </div>
            </>
        ),
    },
    {
        id: '05',
        title: 'THIRD_PARTY_SERVICES',
        body: (
            <>
                <p>
                    Parts of the platform rely on third-party services, including Cloudflare Pages for delivery,
                    Supabase for backend and authentication-related services, Umami for privacy-focused analytics, and
                    external OAuth providers for sign-in. Their availability, processing, and policies can affect how
                    the platform behaves.
                </p>
                <p>
                    Your use of connected third-party services may also be subject to their own terms, privacy notices,
                    and operational limits.
                </p>
                <div className="grid gap-4 md:grid-cols-2">
                    <div className="bg-[#1a1b1b] px-4 py-4 md:px-5 md:py-5">
                        <div className="mb-3 flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.22em] text-[#8cacff]">
                            <MaterialIcon icon="cloud" size={13} className="text-current" />
                            DELIVERY_STACK
                        </div>
                        <p className="text-[12px] leading-6 text-[#8a8f99] md:text-[13px]">
                            Cloudflare Pages and related edge services handle delivery, caching, and request-layer
                            protections.
                        </p>
                    </div>
                    <div className="bg-[#1a1b1b] px-4 py-4 md:px-5 md:py-5">
                        <div className="mb-3 flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.22em] text-[#00cffc]">
                            <MaterialIcon icon="query_stats" size={13} className="text-current" />
                            ANALYTICS_LAYER
                        </div>
                        <p className="text-[12px] leading-6 text-[#8a8f99] md:text-[13px]">
                            Umami is used for privacy-conscious analytics, and other connected providers may process
                            operational data needed for the service to function.
                        </p>
                    </div>
                </div>
            </>
        ),
    },
    {
        id: '06',
        title: 'LIABILITY_LIMITS',
        body: (
            <>
                <p>
                    To the maximum extent allowed by applicable law, STARTZONE.DEV and its operators are not liable for
                    indirect, incidental, special, consequential, or exemplary damages arising from your use of the
                    platform, your reliance on the content, your inability to access the service, or the acts or
                    failures of third-party providers.
                </p>
                <p>
                    If a jurisdiction does not allow certain exclusions, those exclusions apply only to the extent
                    permitted by law.
                </p>
            </>
        ),
    },
    {
        id: '07',
        title: 'CHANGES_AND_CONTACT',
        body: (
            <>
                <p>
                    We may update these terms as the platform evolves. Continued use after changes take effect means you
                    accept the updated terms. If you have questions about these terms or need to raise an account,
                    policy, or deletion request, use the contact route.
                </p>
                <Link
                    to="/contact"
                    className="group inline-flex flex-wrap items-center gap-3 bg-[#131313] p-1 pr-4 transition-[filter,background-color,transform] duration-200 hover:bg-[#171717] hover:brightness-110 active:scale-[0.985]"
                >
                    <span className="bg-[linear-gradient(135deg,#8cacff,#769dff)] px-3 py-2 font-['Space_Grotesk'] text-[11px] font-black uppercase tracking-[0.18em] text-[#001f55] shadow-[0_0_18px_rgba(140,172,255,0.22)]">
                        OPEN_CONTACT_ROUTE
                    </span>
                    <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#8cacff]/65 transition-colors group-hover:text-[#8cacff]">
                        ID: TERMS_SUPPORT_CHANNEL
                    </span>
                </Link>
            </>
        ),
    },
] as const;

export default function TermsPage() {
    return (
        <div className="relative min-h-screen overflow-hidden bg-[#0e0e0e] text-white selection:bg-[#8cacff] selection:text-[#001f55]">
            <SEO
                title="Terms of Use"
                description="startzone.dev terms of use, platform scope, risk disclaimers, and service conditions."
                canonicalUrl="https://startzone.dev/terms"
                schema={termsSchema}
            />

            <div className="pointer-events-none fixed inset-0 z-0">
                <div
                    className="absolute inset-0 opacity-[0.16]"
                    style={{
                        backgroundImage: 'radial-gradient(circle at 1px 1px, #1c1d1d 1px, transparent 0)',
                        backgroundSize: '32px 32px',
                    }}
                />
                <div
                    className="absolute inset-0 opacity-[0.08]"
                    style={{
                        background: 'linear-gradient(to bottom, transparent 50%, rgba(140, 172, 255, 0.05) 50%)',
                        backgroundSize: '100% 4px',
                    }}
                />
                <div className="absolute left-1/2 top-[-8rem] h-[24rem] w-[44rem] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(140,172,255,0.08),transparent_72%)] blur-[120px]" />
                <div className="absolute right-[6%] top-[42%] h-[14rem] w-[14rem] rounded-full bg-[radial-gradient(circle,rgba(255,113,108,0.07),transparent_72%)] blur-[110px]" />
            </div>

            <div className="pointer-events-none absolute left-3 top-24 hidden opacity-[0.12] lg:block">
                <div className="flex flex-col gap-3">
                    <div className="h-1 w-1 bg-[#8cacff]" />
                    <div className="h-16 w-px bg-[#2b2d30]" />
                    <div className="h-1 w-1 bg-[#8cacff]" />
                </div>
            </div>

            <main className="relative z-10 mx-auto w-full max-w-[58rem] px-6 pb-28 pt-12 sm:px-8 md:pt-16 lg:px-10 lg:pb-32">
                <div className="mb-10 flex items-center gap-4 md:mb-14">
                    <Link
                        to="/"
                        className="group inline-flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.26em] text-[#8cacff] transition-all hover:brightness-125"
                    >
                        <MaterialIcon icon="arrow_back" size={14} className="text-current transition-transform group-hover:-translate-x-0.5" />
                        [ RETURN_TO_BASE ]
                    </Link>
                    <div className="h-px flex-1 bg-[#202224]" />
                </div>

                <header className="mb-16 md:mb-20">
                    <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
                        <div className="max-w-[40rem]">
                            <div className="mb-4 inline-flex items-center gap-2 bg-[#1f2d4b] px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-[#d7e4ff]">
                                CONTRACT_LAYER: ACTIVE
                            </div>
                            <h1 className="font-['Space_Grotesk'] text-[3.1rem] font-black uppercase leading-[0.9] tracking-[-0.08em] text-white sm:text-[4.5rem] md:text-[5.2rem]">
                                TERMS OF
                                <br />
                                <span className="text-[#8cacff]">USE</span>
                            </h1>
                        </div>

                        <div className="space-y-1.5 pt-2 font-mono text-[9px] uppercase tracking-[0.22em] text-[#64686f] md:pb-2 md:text-right">
                            <div>
                                <span className="mr-2 text-[#8cacff]">+</span>
                                RISK_POSTURE: USER_ASSUMED
                            </div>
                            <div>
                                <span className="mr-2 text-[#ff716c]">+</span>
                                WARRANTY_STATE: NONE
                            </div>
                            <div>
                                <span className="mr-2 text-[#00cffc]">+</span>
                                VERSION: v1.0.0_terms
                            </div>
                        </div>
                    </div>
                </header>

                <div className="space-y-14 md:space-y-16">
                    {sections.map((section) => (
                        <section key={section.id} className="group relative">
                            <div className="flex items-start gap-4 md:gap-6">
                                <div className="mt-1 hidden shrink-0 select-none font-mono text-[11px] uppercase tracking-[0.22em] text-[#2f3236] md:block">
                                    <div>{section.id}</div>
                                    <div>//</div>
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="mb-5 flex items-center gap-4">
                                        <div className="font-['Space_Grotesk'] text-lg font-black uppercase tracking-[-0.03em] text-white md:text-[1.65rem]">
                                            {section.title}
                                        </div>
                                        <div className="h-px flex-1 bg-[#202224]" />
                                    </div>
                                    <div className="max-w-[42rem] space-y-6 text-[13px] leading-7 text-[#9499a1] md:text-[14px] md:leading-8">
                                        {section.body}
                                    </div>
                                </div>
                            </div>
                        </section>
                    ))}
                </div>

                <section className="mt-20 md:mt-24">
                    <div className="grid gap-4 md:grid-cols-[1.1fr_1fr_1fr]">
                        <div className="flex items-center gap-4 bg-[#131313] px-4 py-4 md:px-5">
                            <div className="flex h-9 w-9 items-center justify-center bg-[#8cacff] text-[#001f55]">
                                <MaterialIcon icon="gavel" size={18} fill className="text-current" />
                            </div>
                            <div>
                                <div className="font-['Space_Grotesk'] text-[11px] font-black uppercase tracking-[0.18em] text-white">
                                    ST_TERMS_MODULE
                                </div>
                                <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#64686f]">
                                    OPERATIONAL_BOUNDARIES_DECLARED
                                </div>
                            </div>
                        </div>

                        <div className="bg-[#131313] px-4 py-4 md:px-5">
                            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#64686f]">PLATFORM_MODE</div>
                            <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.16em] text-[#8cacff]">
                                EDUCATIONAL_ONLY
                            </div>
                        </div>

                        <div className="bg-[#131313] px-4 py-4 md:px-5">
                            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#64686f]">LIABILITY_STATE</div>
                            <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.16em] text-[#ff716c]">
                                LIMITED // AS_ALLOWED_BY_LAW
                            </div>
                        </div>
                    </div>

                    <div className="mt-10 flex flex-wrap items-center justify-between gap-5 border-t border-[#1d1f21] pt-6 font-mono text-[9px] uppercase tracking-[0.2em] text-[#50545a]">
                        <div className="flex flex-wrap items-center gap-5">
                            <Link to="/terms" className="transition-colors hover:text-[#8cacff]">
                                ST_TERMS_MODULE
                            </Link>
                            <Link to="/privacy" className="transition-colors hover:text-[#8cacff]">
                                ST_PRIVACY_PROTOCOLS
                            </Link>
                            <Link to="/contact" className="transition-colors hover:text-[#8cacff]">
                                CONTACT_ROUTE
                            </Link>
                            <span>RISK_DISCLAIMER_ACTIVE</span>
                        </div>
                        <div>[CONTRACT_ACTIVE] © 2026 STARTZONE_OS // CONTINUED_USE_IMPLIES_ACCEPTANCE</div>
                    </div>
                </section>
            </main>
        </div>
    );
}
